import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { customerSession, CUSTOMER_LOGOUT_EVENT } from '../services/api';
import { accountService } from '../services/accountService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => customerSession.get()?.user || null);
  // { reason: 'login' | 'checkout', onDone } while the log-in popup is open (only when the shopper asks for it)
  const [modal, setModal] = useState(null);

  const saveSession = useCallback(({ token, user: u }) => {
    customerSession.set({ token, user: u });
    setUser(u);
  }, []);

  // Keep the cached profile in sync with the user object returned by the server
  const setProfile = useCallback(u => {
    const session = customerSession.get();
    if (session) customerSession.set({ ...session, user: u });
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    customerSession.clear();
    setUser(null);
  }, []);

  // Refresh the profile once per visit; a stale login is cleared by the API client
  useEffect(() => {
    if (customerSession.get()?.token) accountService.me().then(setProfile).catch(() => {});
    const onLogout = () => setUser(null);
    window.addEventListener(CUSTOMER_LOGOUT_EVENT, onLogout);
    return () => window.removeEventListener(CUSTOMER_LOGOUT_EVENT, onLogout);
  }, [setProfile]);

  const openAuth = useCallback((reason = 'login', onDone) => setModal({ reason, onDone }), []);
  const closeAuth = useCallback(() => setModal(null), []);

  const value = useMemo(() => ({
    user,
    isLoggedIn: Boolean(user),
    saveSession,
    setProfile,
    logout,
    authModal: modal,
    openAuth,
    closeAuth,
    finishAuth: session => {
      saveSession(session);
      const done = modal?.onDone;
      setModal(null);
      if (done) done(session.user);
    }
  }), [user, saveSession, setProfile, logout, modal, openAuth, closeAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Outside the provider (e.g. a component rendered on its own in a test) behave as logged out
const LOGGED_OUT = {
  user: null, isLoggedIn: false, authModal: null,
  saveSession: () => {}, setProfile: u => u, logout: () => {}, openAuth: () => {}, closeAuth: () => {}, finishAuth: () => {}
};

export const useAuth = () => useContext(AuthContext) || LOGGED_OUT;
